import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Briefcase, Building, Lock } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function PrivateEquityPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Private Equity Portfolios',
    description: 'Protecting portfolio companies across your investment lifecycle',
    url: 'https://thecisonetwork.com/sectors/private-equity',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Private Equity',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Private Equity Portfolios"
        subtitle="Protecting portfolio companies across your investment lifecycle - from due diligence to exit"
        eyebrow="Private Equity"
        backgroundImage={getSectorCard('private-equity')?.image}
        backgroundAlt={getSectorCard('private-equity')?.imageAlt}
        seoTitle="Fractional CISO Private Equity | The CISO Network"
        seoDescription="Expert fractional CISO services for private equity firms. Secure your portfolio companies, reduce cyber risk, and protect deal value throughout the investment lifecycle."
      />

      {/* Why Private Equity Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Private Equity
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Private equity firms face a unique cybersecurity challenge: managing cyber risk
                  across dozens of portfolio companies with varying maturity levels, often without
                  the in-house expertise to do so effectively. A single breach at one portfolio
                  company can cascade across your entire fund, damaging multiple investments and
                  threatening your reputation with LPs.
                </p>
                <p>
                  Cyber risk has become a deal-breaker in M&A. Acquirers increasingly scrutinise
                  <Link to="/services/risk-assessment" className="text-secondary hover:underline">
                    security postures
                  </Link>{' '}
                  during{' '}
                  <Link
                    to="/packages/ma-cyber-due-diligence"
                    className="text-secondary hover:underline"
                  >
                    due diligence
                  </Link>
                  , and significant vulnerabilities can lead to purchase price reductions, escrow
                  holdbacks, or deal abandonment. For PE firms, this means cybersecurity directly
                  impacts exit multiples and fund returns.
                </p>
                <p>
                  Yet most portfolio companies lack the resources for a full-time CISO, and PE
                  operating partners rarely have deep cybersecurity expertise. This gap leaves
                  portfolio companies exposed and your investments at risk. Our fractional CISO
                  service provides portfolio-wide security leadership at a fraction of the cost of
                  multiple full-time hires.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Briefcase className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Due Diligence to Exit
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Cybersecurity support throughout the investment lifecycle - from pre-deal
                      technical due diligence to value creation and exit preparation.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Building className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Portfolio-Wide Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Flexible engagement models that scale across your portfolio. One CISO can
                      oversee multiple companies, reducing cost while ensuring consistent security
                      standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When Portfolio Companies Are Compromised
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real incidents where PE-backed companies suffered breaches that eroded deal value
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Verizon-Yahoo Deal (2017)',
                impact: '$350M price reduction post-breach discovery',
                description:
                  "Verizon reduced its acquisition price by $350 million after discovering Yahoo's massive data breaches during due diligence. For PE firms, this demonstrates how hidden cyber liabilities can destroy deal value overnight.",
              },
              {
                icon: AlertTriangle,
                title: 'Brookfield-CDK Global (2024)',
                impact: 'Ransomware disrupted operations post-acquisition',
                description:
                  'CDK Global, acquired by Brookfield Business Partners in 2022, suffered a major ransomware attack in June 2024 that disrupted automotive dealership operations nationwide. The incident demonstrated how portfolio companies remain vulnerable to cyber attacks that can damage investment value.',
              },
              {
                icon: AlertTriangle,
                title: 'Addison Healthcare (2023)',
                impact: 'PE-backed firm shut down post-breach',
                description:
                  'This private equity-backed healthcare provider was forced to close after a ransomware attack destroyed its operations. Without adequate cyber insurance and incident response planning, the fund lost its entire investment.',
              },
              {
                icon: AlertTriangle,
                title: 'M&A Cyber Due Diligence Failure',
                impact: '$50M+ in hidden remediation costs',
                description:
                  'A mid-market PE firm acquired a software company only to discover post-close that critical security vulnerabilities required $50M+ in remediation. Proper technical due diligence would have identified these issues before closing.',
              },
              {
                icon: AlertTriangle,
                title: 'SolarWinds Supply Chain (2020)',
                impact: 'Multiple PE portfolio companies affected',
                description:
                  'The SolarWinds attack demonstrated how supply chain compromises cascade through portfolios. PE firms with portfolio-wide security visibility were able to respond faster and limit damage.',
              },
              {
                icon: AlertTriangle,
                title: 'LP Due Diligence Pressure',
                impact: 'Fundraising challenges for GP',
                description:
                  'An increasing number of LPs now require detailed cybersecurity disclosure during fundraising. PE firms without portfolio security programmes struggle to demonstrate adequate risk management to institutional investors.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing Private Equity
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The cyber threats that threaten portfolio value and fund returns
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Deal Value Erosion',
                description:
                  'Undisclosed cyber vulnerabilities discovered during due diligence can lead to price reductions, escrow holdbacks, or deal collapse.',
              },
              {
                icon: Building,
                title: 'Portfolio Contagion',
                description:
                  'Shared infrastructure, common vendors, and interconnected systems mean one breach can spread across multiple portfolio companies.',
              },
              {
                icon: Lock,
                title: 'Ransomware Extortion',
                description:
                  'PE-backed companies are prime ransomware targets - attackers know portfolio companies have capital to pay and cannot afford operational disruption.',
              },
              {
                icon: Briefcase,
                title: 'Regulatory Exposure',
                description:
                  'GDPR, sector-specific regulations, and emerging cyber disclosure requirements create compliance burdens across portfolio companies.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Private Equity - FAQs"
        subtitle="Common questions from PE firms and portfolio companies"
        items={[
          {
            id: 'pe-1',
            question: 'How does a fractional CISO work with private equity portfolio companies?',
            answer:
              'Our CISOs work with PE-backed companies to build security maturity ahead of exit, protect value creation, and satisfy buyer due diligence. They operate on a part-time basis, providing governance and oversight without the overhead of a full-time hire at each portfolio company.',
          },
          {
            id: 'pe-2',
            question: 'How does a fractional CISO help during due diligence?',
            answer:
              'Our CISOs conduct buy-side and sell-side cyber due diligence, identifying material security risks that could affect valuation or deal structure. We have helped PE firms avoid overpaying for compromised assets and helped portfolio companies remediate issues before exit to maximise valuation.',
          },
          {
            id: 'pe-3',
            question: 'Can a fractional CISO support multiple portfolio companies?',
            answer:
              'Yes. This is one of the key advantages of the fractional model for PE firms. One experienced CISO can provide governance and oversight across multiple portfolio companies, standardising security reporting, identifying cross-portfolio risks, and building a security playbook that each company can adapt.',
          },
          {
            id: 'pe-4',
            question: 'What cybersecurity red flags do buyers look for?',
            answer:
              'Buyers increasingly look for evidence of active security governance, not just policies. Key red flags include unpatched critical vulnerabilities, lack of incident response testing, poor access controls, inadequate vendor due diligence, and no board-level security reporting. Our CISOs help portfolio companies address these before they become deal blockers.',
          },
          {
            id: 'pe-5',
            question: 'How does a fractional CISO improve exit valuations?',
            answer:
              'A well-governed security programme reduces buyer risk, shortens due diligence, and eliminates the price discounts that follow discovery of material vulnerabilities. Our CISOs help portfolio companies achieve the security certifications and governance maturity that strategic and financial buyers expect at exit.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Portfolio. Maximise Deal Value.
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Our fractional CISOs understand the unique cybersecurity challenges facing private
            equity. From due diligence to exit, we help protect and enhance the value of your
            investments.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
